/**
 * Method which checks for insufficient balance and redirects to the appropriate page.
 * @param widgetProperties
 * @param injector
 */

import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AccountState } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { BeneficiaryState } from '@app/finxone-web-frontend/app/lib/state/beneficiary.state';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { PaymentsErrorCodes } from '@app/translations';
import { FormActionTypeEnum, SEPAPaymentRailTypeEnum } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import { ActionButtonOutcomesType, BaseWidgetProperties } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { findAndShowAlertFromCode } from '../../../alert-code-utils/alert-code.utils';
import { paymentValidationsForSEPA } from '@finxone-platform/shared/frontend';
import { createRedirectionUrlFromNavigationOptions } from '../../../utils';

export function initiateRedirectionToPaymentSummary(
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
) {
  const store = injector.get(Store);
  const router = injector.get(Router);
  const alertHandlerService = injector.get(AlertHandlerService);

  const navigationConfig = widgetProperties?.['actionButtonNavigationConfig'];

  const formState = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.BENEFICIARY_PAYMENT_CURRENCY_SELECTION),
  )?.formData;
  const activeAccountId = store.selectSnapshot(AccountState.getCurrentAccountId);

  const accountsData = store.selectSnapshot(AccountState.getAccounts)?.accounts;
  if (!formState || !accountsData) {
    Sentry.addBreadcrumb({
      category: 'Initiate payment summary - Unified payments',
      message: 'formData or accountsData is undefined or null',
      level: 'error',
    });
    throw new Error('formData or accountsData is undefined or null');
  }

  if (!formState?.['amount']) {
    // Validate the transaction when amount is invalid.
    findAndShowAlertFromCode(store, router, alertHandlerService, [PaymentsErrorCodes.INVALID_AMOUNT]);
    console.log(formState, accountsData);
    Sentry.addBreadcrumb({
      category: 'Initiate payment summary - Unified payments',
      message: 'Please enter a valid amount to proceed with the payment',
      level: 'error',
    });
    throw new Error('Please enter a valid amount to proceed with the payment.');
  }
  const account = accountsData.find((account) => account.accountId === activeAccountId);
  if (!account) {
    Sentry.addBreadcrumb({
      category: 'Initiate payment summary - Unified payments',
      message: 'Account with activeAccountId not found',
      level: 'error',
    });
    throw new Error('Account with activeAccountId not found');
  }

  const amountPlusFees = Number(formState?.['totalAmount'] || formState?.['amount']);
  const accountBalance = Number(account.balance);
  if (isNaN(accountBalance) || isNaN(amountPlusFees)) {
    Sentry.addBreadcrumb({
      category: 'Initiate payment summary - Unified payments',
      message: 'Account balance or payment amount NaN',
      level: 'error',
    });
    throw new Error('Account balance or payment amount NaN');
  }

  const isBalanceAvailable = accountBalance >= amountPlusFees;

  if (!isBalanceAvailable) {
    findAndShowAlertFromCode(store, router, alertHandlerService, [PaymentsErrorCodes.INSUFFICIENT_BALANCE]);
    if (navigationConfig?.[ActionButtonOutcomesType.FAILURE]) {
      const redirectUrl = createRedirectionUrlFromNavigationOptions(
        navigationConfig[ActionButtonOutcomesType.FAILURE],
      );
      router.navigateByUrl(redirectUrl);
    }
    throw new Error('Enter a lower amount to continue.');
  }

  /**
   * SEPA transaction validation only works with @default {EURO}
   */
  if (account?.currency === 'EUR') {
    validateSEPATransactionCombined(
      store,
      router,
      alertHandlerService,
      amountPlusFees,
      formState?.['selectedBeneficiaryId'],
    );
  }
  if (navigationConfig?.[ActionButtonOutcomesType.SUCCESS]) {
    const redirectUrl = createRedirectionUrlFromNavigationOptions(
      navigationConfig[ActionButtonOutcomesType.SUCCESS],
    );
    router.navigateByUrl(redirectUrl);
  }
}

/**
 * Validates SEPA Transaction including Target2 payment rail requirements - Combined Version.
 * @param {Store} store - The application store instance.
 * @param {Router} router - Angular router instance for navigation.
 * @param {AlertHandlerService} alertHandlerService - Service to handle alert messages.
 * @param {number} amountPlusFees - Total amount including fees to validate.
 * @param {string} bid - Beneficiary ID for validation.
 * @throws Will throw an error if any SEPA validations fail.
 */
const validateSEPATransactionCombined = (
  store: Store,
  router: Router,
  alertHandlerService: AlertHandlerService,
  amountPlusFees: number,
  bid: string,
) => {
  // Get payment rail type from the store
  const paymentRail = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.SEPA_PAYMENT),
  )?.formData?.paymentRail as SEPAPaymentRailTypeEnum;

  const toggleRailSelector = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.SEPA_PAYMENT_RAIL_FLAG),
  )?.formData?.toggleRailSelector;

  // Fetch beneficiary details based on the provided bid
  const beneficiaryList = store.selectSnapshot(BeneficiaryState.getBeneficiary);
  const beneficiaryDetails = beneficiaryList?.items?.find((v) => v.id === bid);

  // Perform SEPA payment rail validations
  if (toggleRailSelector && paymentRail) {
    const paymentValidations = paymentValidationsForSEPA[paymentRail];

    // Validate minimum and maximum transaction limits
    if (amountPlusFees < 0) {
      findAndShowAlertFromCode(store, router, alertHandlerService, [PaymentsErrorCodes.AMOUNT_TOO_LOW]);
      throw new Error(`The entered amount is below the minimum allowed limit.`);
    } else if (amountPlusFees > Number(paymentValidations?.maximumTransactionLimit)) {
      findAndShowAlertFromCode(store, router, alertHandlerService, [
        PaymentsErrorCodes.EXCEEDED_TRANSFER_LIMIT,
      ]);
      Sentry.addBreadcrumb({
        category: 'Initiate payment summary - Unified payments',
        message: `The transfer amount exceeds the allowed limit for ${paymentRail}.`,
        level: 'error',
      });
      throw new Error(`The transfer amount exceeds the allowed limit for ${paymentRail}.`);
    }

    // Validate additional requirements for the Target2 payment rail
    if (paymentRail === SEPAPaymentRailTypeEnum.TARGET2) {
      // Validate BIC for Target2 payments
      if (!beneficiaryDetails?.destinationIdentifier?.bic) {
        findAndShowAlertFromCode(store, router, alertHandlerService, [
          PaymentsErrorCodes.PAYMENT_FAILED_BIC_REQUIRED,
        ]);
        Sentry.addBreadcrumb({
          category: 'Initiate payment summary - Unified payments',
          message: `Mandatory field missing: BIC is required for Target2 payments. Please complete the required fields and try again.`,
          level: 'error',
        });
        throw new Error(
          'Mandatory field missing: BIC is required for Target2 payments. Please complete the required fields and try again.',
        );
      }

      // Validate address details for Target2 payments
      if (!beneficiaryDetails?.address || Object.keys(beneficiaryDetails?.address).length === 0) {
        findAndShowAlertFromCode(store, router, alertHandlerService, [
          PaymentsErrorCodes.PAYMENT_FAILED_ADDRESS_REQUIRED,
        ]);
        Sentry.addBreadcrumb({
          category: 'Initiate payment summary - Unified payments',
          message: `Mandatory field missing: Address is required for Target2 payments. Please complete the required fields and try again.`,
          level: 'error',
        });
        throw new Error(
          'Mandatory field missing: Address is required for Target2 payments. Please complete the required fields and try again.',
        );
      }
    }
  }
};
